import React, { useState, useEffect } from 'react';
import axiosInstance from '../store/axiosInterceptor';
import { useToast } from '../components/Toast/ToastContext';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField, Button, MenuItem, Select, FormControl } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CardSkeleton } from '../components/Skeleton';

interface Transaction {
  payout_id: string;
  coach_id: string;
  coach_name?: string;
  coach_email?: string;
  amount: number;
  payout_status: string;
  created_at: string;
  related_booking_id?: string;
  session_id?: string;
}

interface Coach {
  id: string;
  name: string;
  email: string;
}

// Common timezones
const TIMEZONES = [
  { label: 'UTC', value: 'UTC' },
  { label: 'GMT', value: 'GMT' },
  { label: 'US/Eastern', value: 'US/Eastern' },
  { label: 'US/Central', value: 'US/Central' },
  { label: 'US/Mountain', value: 'US/Mountain' },
  { label: 'US/Pacific', value: 'US/Pacific' },
  { label: 'US/Alaska', value: 'US/Alaska' },
  { label: 'US/Hawaii', value: 'US/Hawaii' },
  { label: 'Europe/London', value: 'Europe/London' },
  { label: 'Europe/Paris', value: 'Europe/Paris' },
  { label: 'Europe/Berlin', value: 'Europe/Berlin' },
  { label: 'Asia/Dubai', value: 'Asia/Dubai' },
  { label: 'Asia/Kolkata', value: 'Asia/Kolkata' },
  { label: 'Asia/Bangkok', value: 'Asia/Bangkok' },
  { label: 'Asia/Singapore', value: 'Asia/Singapore' },
  { label: 'Asia/Hong_Kong', value: 'Asia/Hong_Kong' },
  { label: 'Asia/Tokyo', value: 'Asia/Tokyo' },
  { label: 'Australia/Sydney', value: 'Australia/Sydney' },
  { label: 'Australia/Melbourne', value: 'Australia/Melbourne' },
];

const PayoutsPage: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
  const [formData, setFormData] = useState({
    coach_id: '',
    amount: '',
    datetime: null as any,
    timezone: 'UTC',
    payout_status: 'Pending',
    transaction_id: '',
    payment_type: '',
    payment_method: '',
  });
  const { showToast } = useToast();

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        
        // Fetch coaches and all payouts in parallel - only 2 API calls total
        const [coachesResponse, payoutsResponse] = await Promise.all([
          axiosInstance.get('/api/coaches'),
          axiosInstance.get('/api/admin/payouts')
        ]);
        
        const coachesData = coachesResponse.data || [];
        const payoutsData = payoutsResponse.data || [];
        
        setCoaches(coachesData);
        
        // Create a map of coaches for quick lookup
        const coachMap = new Map<string, Coach>(coachesData.map((c: Coach) => [c.id, c]));
        
        // Enrich payouts with coach information
        const enrichedPayouts = payoutsData.map((p: any) => {
          const coach = coachMap.get(p.coach_id);
          return {
            ...p,
            coach_name: coach?.name || 'Unknown',
            coach_email: coach?.email || 'N/A',
          };
        });
        
        setTransactions(enrichedPayouts);
      } catch (error: any) {
        showToast('Failed to load data', 'error');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [showToast]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/api/coaches');
      const coachesData = response.data || [];
      
      // Fetch payouts for all coaches in parallel
      const payoutPromises = coachesData.map(async (coach: Coach) => {
        try {
          const payoutsResponse = await axiosInstance.get(`/api/admin/coaches/${coach.id}/payouts`);
          const payouts = payoutsResponse.data;
          
          // Handle null or empty response
          if (!payouts || !Array.isArray(payouts) || payouts.length === 0) {
            return [];
          }
          
          return payouts
            .filter((p: any) => p && p.payout_status && p.payout_status.trim() !== '')
            .map((p: any) => ({
              ...p,
              coach_name: coach.name,
              coach_email: coach.email,
            }));
        } catch (err) {
          // Skip coaches without payouts or with errors
          return [];
        }
      });
      
      const allPayoutsArrays = await Promise.all(payoutPromises);
      const allPayouts = allPayoutsArrays.flat();
      setTransactions(allPayouts);
    } catch (error: any) {
      showToast('Failed to fetch transactions', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTransaction = async () => {
    if (!formData.coach_id || !formData.amount || !formData.transaction_id || !formData.payment_type || !formData.datetime || !formData.timezone) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    try {
      const resolvedPaymentMethod = formData.payment_method || formData.payment_type || 'Bank Transfer';
      const payload = {
        coach_id: formData.coach_id,
        amount: parseFloat(formData.amount),
        payout_status: formData.payout_status.toLowerCase(),
        created_at: formData.datetime.toISOString(),
        timezone: formData.timezone,
        payout_id: formData.transaction_id,
        payment_type: formData.payment_type,
        payment_method: resolvedPaymentMethod,
      };

      await axiosInstance.post(`/api/admin/coaches/${formData.coach_id}/payouts`, payload);
      showToast('Transaction added successfully', 'success');
      setShowAddModal(false);
      setFormData({
        coach_id: '',
        amount: '',
        datetime: null,
        timezone: 'UTC',
        payout_status: 'Pending',
        transaction_id: '',
        payment_type: '',
        payment_method: '',
      });
      fetchTransactions();
    } catch (error: any) {
      showToast('Failed to add transaction', 'error');
    }
  };

  const handleEditTransaction = async () => {
    if (!selectedTransaction) return;

    try {
      const payload = {
        payout_id: selectedTransaction.payout_id,
        coach_id: selectedTransaction.coach_id,
        amount: parseFloat(formData.amount) || selectedTransaction.amount,
        payout_status: (formData.payout_status || selectedTransaction.payout_status).toLowerCase(),
        created_at: formData.datetime ? formData.datetime.toISOString() : selectedTransaction.created_at,
        timezone: formData.timezone,
      };

      await axiosInstance.put(
        `/api/admin/coaches/${selectedTransaction.coach_id}/payouts/${selectedTransaction.payout_id}`,
        payload
      );
      showToast('Transaction updated successfully', 'success');
      setShowEditModal(false);
      setSelectedTransaction(null);
      setFormData({
        coach_id: '',
        amount: '',
        datetime: null,
        timezone: 'UTC',
        payout_status: 'Pending',
        transaction_id: '',
        payment_type: '',
        payment_method: '',
      });
      fetchTransactions();
    } catch (error: any) {
      showToast('Failed to update transaction', 'error');
    }
  };



  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'paid':
        return '#10b981';
      case 'pending':
        return '#f59e0b';
      case 'failed':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const filteredTransactions = transactions;
  return (
    <div className="space-y-6">
      {/* Header */}
      {!showAddModal &&
        <div className="flex items-center justify-between">
          <div className="relative w-96">
            <svg className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              placeholder="Search transactions..."
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 placeholder-gray-400 focus:outline-none focus:border-gray-300"
            />
          </div>
          
          <button
            onClick={() => {
              setShowAddModal(true);
              setFormData({
                coach_id: '',
                amount: '',
                datetime: null,
                timezone: 'UTC',
                payout_status: 'Pending',
                transaction_id: '',
                payment_type: '',
                payment_method: '',
              });
            }}
            className="flex items-center gap-2 px-4 py-2  bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            Add Transaction
          </button>
        </div>
}
    

      {/* Loading State */}
      {loading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <CardSkeleton key={i} />
          ))}
        </div>
      )}

      {/* List */}
      {!loading && !showAddModal && filteredTransactions.length > 0 && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white shadow-[0_4px_12px_0_rgba(249,250,251,1)] overflow-hidden">
          <div className="grid h-12 grid-cols-[140px_90px_1.4fr_110px_110px_140px_1fr_48px] gap-2 border-b border-gray-100 bg-gray-50 px-6 py-3 text-xs font-semibold text-gray-500">
            <span>Date</span>
            <span>Time</span>
            <span>Coach</span>
            <span>Amount</span>
            <span>Status</span>
            <span>Payment type</span>
            <span>Transaction ID</span>
            <span className="text-right"></span>
          </div>
          <div className="divide-y divide-gray-100">
            {filteredTransactions.map((transaction) => {
              const coachInitials = (transaction.coach_name || 'Unknown')
                .split(' ')
                .map((n) => n[0])
                .join('')
                .slice(0, 2)
                .toUpperCase();
              return (
                <div
                  key={transaction.payout_id}
                  className="grid h-16 grid-cols-[140px_90px_1.4fr_110px_110px_140px_1fr_48px] items-center gap-2 px-6 py-3 text-sm text-gray-900"
                >
                  <span className="text-gray-900">
                    {new Date(transaction.created_at).toLocaleDateString('en-US', {
                      month: 'long',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </span>
                  <span className="text-gray-900">
                    {new Date(transaction.created_at).toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-semibold text-gray-700">
                      {coachInitials}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{transaction.coach_name || 'Unknown'}</p>
                      <p className="text-xs text-gray-500">{transaction.coach_email || 'N/A'}</p>
                    </div>
                  </div>
                  <span className="font-semibold text-gray-900">${transaction.amount.toFixed(2)}</span>
                  <span
                    className="inline-flex w-fit items-center rounded-full px-2.5 py-1 text-xs font-semibold"
                    style={{
                      color: getStatusColor(transaction.payout_status || 'pending'),
                      backgroundColor: `${getStatusColor(transaction.payout_status || 'pending')}1A`,
                    }}
                  >
                    {transaction.payout_status
                      ? transaction.payout_status.charAt(0).toUpperCase() + transaction.payout_status.slice(1)
                      : 'Pending'}
                  </span>
                  <span className="text-gray-700">Bank Transfer</span>
                  <span className="text-gray-500 font-mono text-xs">{transaction.payout_id}</span>
                  {/* <div className="relative flex justify-end">
                    <button
                      onClick={(e) => setAnchorEl(e.currentTarget)}
                      className="rounded-md p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                      aria-label="Open actions"
                    >
                      <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path d="M10 3a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5.5a1.5 1.5 0 110 3 1.5 1.5 0 010-3z" />
                      </svg>
                    </button>
                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl)}
                      onClose={() => setAnchorEl(null)}
                    >
                      <MenuItem
                        onClick={() => {
                          handleDownloadReceipt(transaction);
                          setAnchorEl(null);
                        }}
                      >
                        Download Receipt
                      </MenuItem>
                      <MenuItem
                        onClick={() => {
                          handleEditClick(transaction);
                          setAnchorEl(null);
                        }}
                      >
                        Edit Info
                      </MenuItem>
                    </Menu>
                  </div> */}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && !showAddModal && filteredTransactions.length === 0 && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white py-12 text-center shadow-[0_4px_12px_0_rgba(249,250,251,1)]">
          <p className="text-sm text-gray-400">No transactions found</p>
        </div>
      )}

      {/* Add Transaction Form */}
      {showAddModal && (
        <div className="rounded-xl border border-[#F2F4F7] bg-white p-8 shadow-[0_4px_12px_0_rgba(249,250,251,1)] relative z-50 overflow-visible">
          <div className="space-y-8 overflow-visible">
            <div>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="flex items-center justify-center w-8 h-8 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <svg className="w-4 h-4 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                  </svg>
                </button>
                <h3 className="text-sm font-semibold text-gray-700">Coach Information</h3>
              </div>
            
              <div className="mt-3 border-t border-gray-200 pt-4 overflow-visible">
                <FormControl fullWidth size="small">
                  <Select
                    value={formData.coach_id}
                    onChange={(e) => setFormData({ ...formData, coach_id: e.target.value })}
                    displayEmpty
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span style={{ color: '#9CA3AF' }}>Select Coach</span>;
                      }
                      const coach = coaches.find(c => c.id === selected);
                      return coach ? `${coach.name} (${coach.email})` : selected;
                    }}
                    sx={{
                      borderRadius: '0.5rem',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D1D5DB',
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9CA3AF',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          zIndex: 9999,
                        },
                      },
                    }}
                  >
                    <MenuItem value="" disabled>Select Coach</MenuItem>
                    {coaches.map((coach) => (
                      <MenuItem key={coach.id} value={coach.id}>
                        {coach.name} ({coach.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-700">Transaction Information</h3>
              <div className="mt-3 border-t border-gray-200 pt-4 space-y-3 overflow-visible">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 overflow-visible">
                  <div className="flex gap-2 overflow-visible">
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">Timezone*</label>
                      <FormControl fullWidth size="small">
                        <Select
                          value={formData.timezone}
                          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                          displayEmpty
                          renderValue={(selected) => {
                            if (!selected) {
                              return <span style={{ color: '#9CA3AF' }}>Select</span>;
                            }
                            return selected;
                          }}
                          sx={{
                            borderRadius: '0.5rem',
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#E5E7EB',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#D1D5DB',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#9CA3AF',
                            },
                          }}
                          MenuProps={{
                            PaperProps: {
                              sx: {
                                maxHeight: 300,
                                zIndex: 9999,
                              },
                            },
                          }}
                        >
                          <MenuItem value="" disabled>Select</MenuItem>
                          {TIMEZONES.map((tz) => (
                            <MenuItem key={tz.value} value={tz.value}>
                              {tz.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </div>
                    <div className="flex-1">
                      <label className="mb-1 block text-xs font-semibold text-gray-500">Date & Time*</label>
                      <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <DateTimePicker
                          value={formData.datetime}
                          onChange={(newValue) => {
                            setFormData({ ...formData, datetime: newValue });
                          }}
                          slotProps={{
                            textField: {
                              fullWidth: true,
                              size: 'small',
                              InputProps: {
                                className: 'text-sm',
                              },
                              sx: {
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: '0.75rem',
                                  backgroundColor: '#FFFFFF',
                                  '& fieldset': {
                                    borderColor: '#E5E7EB',
                                  },
                                  '&:hover fieldset': {
                                    borderColor: '#000000',
                                  },
                                  '&.Mui-focused fieldset': {
                                    borderColor: '#000000',
                                  },
                                },
                                '& .MuiOutlinedInput-input': {
                                  padding: '10px 12px',
                                  fontSize: '0.875rem',
                                  color: '#374151',
                                },
                              },
                            },
                          }}
                        />
                      </LocalizationProvider>
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">Amount*</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-700 text-sm font-semibold">$</span>
                      <input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="w-full rounded-lg border border-gray-200 bg-white pl-8 pr-3 py-2 text-sm text-gray-700 focus:border-gray-300 focus:outline-none"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">Payment Status*</label>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.payout_status}
                        onChange={(e) => setFormData({ ...formData, payout_status: e.target.value })}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#9CA3AF' }}>Select Payment Status</span>;
                          }
                          return selected;
                        }}
                        sx={{
                          borderRadius: '0.5rem',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D1D5DB',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#9CA3AF',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              zIndex: 9999,
                            },
                          },
                        }}
                      >
                        <MenuItem value="Paid">Paid</MenuItem>
                        <MenuItem value="Pending">Pending</MenuItem>
                        <MenuItem value="Failed">Failed</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 overflow-visible">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">Payment Type*</label>
                    <FormControl fullWidth size="small">
                      <Select
                        value={formData.payment_type}
                        onChange={(e) => setFormData({ ...formData, payment_type: e.target.value })}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) {
                            return <span style={{ color: '#9CA3AF' }}>Select</span>;
                          }
                          return selected;
                        }}
                        sx={{
                          borderRadius: '0.5rem',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#E5E7EB',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#D1D5DB',
                          },
                          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#9CA3AF',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 300,
                              zIndex: 9999,
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled>Select Payment Type</MenuItem>
                        <MenuItem value="Bank Transfer">Bank Transfer</MenuItem>
                        <MenuItem value="Card">Card</MenuItem>
                        <MenuItem value="Cash">Cash</MenuItem>
                        <MenuItem value="Check">Check</MenuItem>
                        <MenuItem value="Other">Other</MenuItem>
                      </Select>
                    </FormControl>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-gray-500">Transaction ID*</label>
                    <input
                      type="text"
                      value={formData.transaction_id}
                      onChange={(e) => setFormData({ ...formData, transaction_id: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-gray-300 focus:outline-none"
                      placeholder="ID"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={handleAddTransaction}
                disabled={!formData.coach_id || !formData.amount || !formData.transaction_id || !formData.payment_type || !formData.datetime || !formData.timezone}
                className="inline-flex items-center gap-2 rounded-lg bg-black px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
              >
                <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                  <path d="M10 4a1 1 0 011 1v4h4a1 1 0 110 2h-4v4a1 1 0 11-2 0v-4H5a1 1 0 110-2h4V5a1 1 0 011-1z" />
                </svg>
                Add Transaction
              </button>
              <button
                onClick={() => setShowAddModal(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Transaction Modal */}
      <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} maxWidth="sm" fullWidth>
        <DialogTitle className="font-bold text-gray-900">Edit Transaction</DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
            fullWidth
            size="small"
            inputProps={{ step: '0.01' }}
          />
          <TextField
            select
            label="Status"
            value={formData.payout_status}
            onChange={(e) => setFormData({ ...formData, payout_status: e.target.value })}
            fullWidth
            size="small"
          >
            <MenuItem value="Paid">Paid</MenuItem>
            <MenuItem value="Pending">Pending</MenuItem>
            <MenuItem value="Failed">Failed</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button onClick={() => setShowEditModal(false)} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleEditTransaction} variant="contained" sx={{ bgcolor: '#2563eb' }}>
            Update Transaction
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PayoutsPage;
